import React, { useState, useCallback } from 'react';
import { DropdownMenuItem } from '../../ui/dropdown-menu';
import { useLazyCommand, useDomainEvent } from '@soap/modules';
import { translate } from '@soap/modules';
import { Archive, X } from 'lucide-react';

interface ArchiveStudentDialogProps {
  studentId: string;
  studentName: string;
}

export function ArchiveStudentDialog({ studentId, studentName }: ArchiveStudentDialogProps) {
  const [open, setOpen] = useState(false);
  const [sendArchiveCommand] = useLazyCommand('C163v1_ArchiveStudent');

  const handleArchive = async () => {
    await sendArchiveCommand({
      C163_StudentId: studentId
    });
    close();
  };

  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });

  const close = useCallback(() => {
    setOpen(false)
    triggerCloseContextMenu();
  },[setOpen]);

  if (!open) {
    return (
      <DropdownMenuItem
        className="w-full justify-start"
        data-dialog-item
        onClick={() => setOpen(true)}
      >
        <Archive className="w-4 h-4 mr-2" />
        {translate("Archive")}
      </DropdownMenuItem>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">{translate("Archive Student")}</h2>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-destructive-action/10 p-4 rounded-lg border border-destructive-action/30">
              <p className="text-sm text-destructive-action-dark">
                {`${translate("This action cannot be undone")}.`} <br/>
                {translate("Please confirm you want to archive the student")} <strong>{studentName}</strong>?
              </p>
            </div>

            <div className="space-y-3">
              <div className="bg-action-blue/10 p-3 rounded-lg border border-action-blue/30">
                <h4 className="font-medium text-action-blue-dark mb-2">{translate("Archived students")}</h4>
                <ul className="ml-4 space-y-1">
                  <li className="text-sm text-action-blue-dark">• {translate("can be viewed using the Include Archived switch")}</li>
                  <li className="text-sm text-action-blue-dark">• {translate("cannot be edited")}</li>
                  <li className="text-sm text-action-blue-dark">• cannot login</li>
                </ul>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg border">
                <div className="text-sm text-gray-600 space-y-1">
                  <div>• {translate("Shared Assessment links are revoked for Archived Students")}</div>
                  <div>• {translate("Assessment Results will remain visible for Archived students")}</div>
                  <div>• {translate("Historical Data such as Archived Items are counted against your plan limits")}</div>
                  <div>• {translate("If you wish to remove a student altogether, use Delete instead")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-5">
            <button
              onClick={close}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {translate("Cancel")}
            </button>
            <button
              onClick={handleArchive}
              className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors"
            >
              {translate("Archive")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
